import React, {
  ReactNode,
  RefObject,
  SyntheticEvent,
  useCallback,
  isValidElement,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import clsx from 'clsx';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  InputProps as StandardInputProps,
} from '@mui/material/Input';
import SearchIcon from 'core/icons/search';
import isEmptyAutoCompleteValue from 'core/components/AutoComplete/utils';

export type AutoCompleteValue<T> = AutocompleteProps<T, boolean, boolean, boolean>['value'];

export type Props<T> = Pick<
    AutocompleteProps<T, boolean, boolean, boolean>,
  'className'
    | 'options'
    | 'renderOption'
    | 'disabled'
    | 'multiple'
    | 'autoComplete'
    | 'loading'
    | 'loadingText'
    | 'openOnFocus'
    | 'includeInputInList'
    | 'filterSelectedOptions'
    | 'noOptionsText'
    | 'blurOnSelect'
    | 'popupIcon'
    | 'freeSolo'
    | 'open'
    | 'clearOnBlur'
    | 'disablePortal'
    | 'onBlur'
    | 'onFocus'
    | 'onOpen'
    | 'onClose'
    | 'onInputChange'
    | 'filterOptions'
    | 'isOptionEqualToValue'
    | 'selectOnFocus'
    | 'classes'
    | 'inputValue'
    | 'groupBy'
    | 'renderGroup'
    | 'clearIcon'
    | 'onHighlightChange'
    | 'onKeyDown'
    | 'autoHighlight'
    | 'slotProps'
    | 'disableClearable'
    | 'disableCloseOnSelect'
    | 'slots'
  >
  & {
  id?: string;
  label?: string;
  error?: boolean;
  required?: boolean;
  placeholder?: ReactNode;
  getLabel: (option: T) => string;
  value?: AutoCompleteValue<T>;
  defaultValue?: AutoCompleteValue<T>;
  inputRef?: RefObject<HTMLInputElement | null>;
  readonly?: boolean;
  dropdownClassName?: string;
  InputProps?: Partial<StandardInputProps>;
  additionalEndAdornment?: ReactNode;
  classes?: Partial<Record<'placeholder', string>>;
  onChange: (
    value: T | T[],
    reason?: AutocompleteChangeReason,
    event?: SyntheticEvent,
  ) => void;
  disableRemove?: boolean;
  getOptionDisabled?: (option: T) => boolean;
  normalizeOption?: (option: T) => T;
  renderTags?: (value: T[]) => ReactNode;
  renderValue?: (value: T[] | T) => ReactNode;
} & Pick<StandardInputProps, 'inputProps'>;

type KeyboardEvent = ReactKeyboardEvent<HTMLDivElement> & {
  defaultMuiPrevented?: boolean;
};

/**
 * Internal AutoComplete component for building lookup components.
 *
 * @internal Use PopulateLookup or GenericLookup for end-user components
 * Public lookup components:
 * - PopulateLookup: For EMR forms with custom rendering
 * - GenericLookup: For general-purpose lookups with URL configuration
 */
export default function AutoCompletedInput<T>({
  id,
  open,
  className,
  label,
  required,
  getLabel,
  inputRef,
  classes,
  readonly,
  dropdownClassName,
  InputProps = {},
  additionalEndAdornment,
  onChange,
  options = [],
  multiple = false,
  filterSelectedOptions = true,
  autoComplete = false,
  includeInputInList = false,
  loading = false,
  clearOnBlur = true,
  openOnFocus = false,
  loadingText = 'Loading...',
  noOptionsText = 'No Options',
  renderTags: _renderTags,
  renderValue: externalRenderValue,
  popupIcon,
  value,
  defaultValue,
  placeholder,
  disabled,
  disableRemove = false,
  getOptionDisabled,
  normalizeOption = (option) => option,
  clearIcon = null,
  inputProps,
  slotProps = {},
  error,
  selectOnFocus = false,
  autoHighlight = true,
  onKeyDown,
  ...props
}: Props<T>) {
  const autoCompleteClasses = clsx('populate-autocomplete', className, { disabled });

  const handleChange = (
    event: SyntheticEvent,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>,
  ) => {
    // When values are handled and displayed outside of mui input,
    // disable removing with backspace | delete
    if (reason === 'removeOption' && disableRemove) return;

    if ((reason === 'createOption' || reason === 'selectOption') && details?.option) {
      const normalized = normalizeOption(details.option);
      const nextValue = Array.isArray(value)
        ? [
          ...(value.slice(0, value.length - 1)),
          normalized,
        ]
        : normalized;

      onChange(nextValue, reason, event);
    } else {
      onChange(value as T | T[], reason, event);
    }
  };

  const defaultRenderValue = useCallback((value: T[] | T): ReactNode => {
    if (Array.isArray(value)) {
      return value.map((v: T) => (
        <span
          key={getLabel(v)}
          className="mr-1"
        >
          {getLabel(v)}
        </span>
      ));
    }

    return getLabel(value);
  }, [getLabel]);

  const hasInputValue = !isEmptyAutoCompleteValue(props.inputValue);
  const renderValue = externalRenderValue
    ?? (multiple ? _renderTags ?? defaultRenderValue : undefined);

  const icon = typeof popupIcon === 'undefined' ? <SearchIcon className="search-icon" /> : popupIcon;
  const hasSelectedValue = !isEmptyAutoCompleteValue(value);
  const customPlaceholder = isValidElement(placeholder)
    ? placeholder
    : null;
  const shouldRenderCustomPlaceholder = !hasInputValue && !!customPlaceholder;
  const hasValue = hasInputValue || (hasSelectedValue && !shouldRenderCustomPlaceholder);
  let resolvedPlaceholder = '';

  if (!shouldRenderCustomPlaceholder && !hasValue && typeof placeholder === 'string') {
    resolvedPlaceholder = placeholder;
  }

  const popperSlotProps = typeof slotProps.popper === 'function'
    ? undefined
    : slotProps.popper;

  return (
    <Autocomplete
      {...props}
      open={open}
      autoHighlight={autoHighlight}
      id={id}
      disabled={disabled}
      className={autoCompleteClasses}
      clearIcon={clearIcon}
      multiple={multiple}
      onChange={handleChange}
      getOptionDisabled={getOptionDisabled}
      value={value as AutocompleteProps<T, boolean, boolean, boolean>['value']}
      defaultValue={defaultValue as AutocompleteProps<T, boolean, boolean, boolean>['defaultValue']}
      options={options}
      getOptionLabel={(option) => getLabel(option as T)}
      popupIcon={!readonly && icon}
      renderValue={renderValue as AutocompleteProps<T, boolean, boolean, boolean>['renderValue']}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      classes={classes}
      readOnly={readonly}
      slotProps={{
        paper: { className: dropdownClassName ?? '' },
        ...slotProps,
        popper: {
          ...popperSlotProps,
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 4] },
            },
            ...(popperSlotProps?.modifiers ?? []),
          ],
        },
      }}
      clearOnBlur={clearOnBlur}
      openOnFocus={openOnFocus}
      selectOnFocus={selectOnFocus}
      filterSelectedOptions={filterSelectedOptions}
      onKeyDown={(event: KeyboardEvent) => {
        onKeyDown?.(event);
        if (!event.defaultMuiPrevented
          && event.key === 'Enter'
          && options?.length === 0
          && !props.freeSolo) {
          event.preventDefault();
          event.defaultMuiPrevented = true;
        }
      }}
      renderInput={(params) => {
        const {
          slotProps: paramsSlotProps,
          InputProps: legacyInputProps,
          inputProps: legacyHtmlInputProps,
          ...textFieldProps
        } = params as typeof params & {
          InputProps?: Record<string, unknown> & {
            endAdornment?: ReactNode;
          };
          inputProps?: Record<string, unknown>;
        };
        const paramsInputSlotProps = paramsSlotProps?.input as
          | (Record<string, unknown> & { endAdornment?: ReactNode })
          | undefined;
        const existingEndAdornment = InputProps.endAdornment
          ?? paramsInputSlotProps?.endAdornment
          ?? legacyInputProps?.endAdornment;

        return (
          <div className="relative">
            <TextField
              {...textFieldProps}
              id={id}
              inputRef={inputRef}
              placeholder={resolvedPlaceholder}
              slotProps={{
                ...(paramsSlotProps ?? {}),
                htmlInput: {
                  ...(legacyHtmlInputProps ?? {}),
                  ...(paramsSlotProps?.htmlInput ?? {}),
                  ...inputProps,
                },
                input: {
                  ...(legacyInputProps ?? {}),
                  ...(paramsInputSlotProps ?? {}),
                  ...InputProps,
                  endAdornment: additionalEndAdornment ? (
                    <>
                      {additionalEndAdornment}
                      {existingEndAdornment}
                    </>
                  ) : existingEndAdornment,
                },
              }}
            />
            {shouldRenderCustomPlaceholder ? (
              <div
                className={clsx(
                  'pointer-events-none absolute inset-y-0 left-3 flex items-center',
                  classes?.placeholder,
                )}
              >
                {customPlaceholder}
              </div>
            ) : null}
          </div>
        );
      }}
    />
  );
}
